import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

export const ADMIN_TIME_ZONES = [
  { value: 'Europe/Prague', label: 'Prague', detail: 'CET/CEST' },
  { value: 'UTC', label: 'UTC', detail: 'Coordinated' },
  { value: 'Europe/Moscow', label: 'Moscow', detail: 'MSK' },
  { value: 'Europe/London', label: 'London', detail: 'GMT/BST' },
  { value: 'America/New_York', label: 'New York', detail: 'ET' },
  { value: 'Asia/Dubai', label: 'Dubai', detail: 'GST' },
] as const;

export type AdminTimeZone = (typeof ADMIN_TIME_ZONES)[number]['value'];

const DEFAULT_ADMIN_TIME_ZONE: AdminTimeZone = 'Europe/Prague';
const STORAGE_KEY = 'adminTimeZone';

interface AdminTimeContextValue {
  timeZone: AdminTimeZone;
  setTimeZone: (timeZone: AdminTimeZone) => void;
  formatDateTime: (value?: string | null) => string;
}

const AdminTimeContext = createContext<AdminTimeContextValue | null>(null);

function normalizeD1Date(value: string) {
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)) {
    return `${value.replace(' ', 'T')}Z`;
  }

  return value;
}

function isAdminTimeZone(value: string | null): value is AdminTimeZone {
  return ADMIN_TIME_ZONES.some((zone) => zone.value === value);
}

export function AdminTimeProvider({ children }: { children: ReactNode }) {
  const [timeZone, setStoredTimeZone] = useState<AdminTimeZone>(() => {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return isAdminTimeZone(saved) ? saved : DEFAULT_ADMIN_TIME_ZONE;
  });

  const value = useMemo<AdminTimeContextValue>(
    () => ({
      timeZone,
      setTimeZone: (nextTimeZone) => {
        window.localStorage.setItem(STORAGE_KEY, nextTimeZone);
        setStoredTimeZone(nextTimeZone);
      },
      formatDateTime: (dateValue) => {
        if (!dateValue) return 'Never';

        const date = new Date(normalizeD1Date(dateValue));
        if (Number.isNaN(date.getTime())) return dateValue;

        return new Intl.DateTimeFormat('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
          timeZone,
          timeZoneName: 'short',
        }).format(date);
      },
    }),
    [timeZone],
  );

  return <AdminTimeContext.Provider value={value}>{children}</AdminTimeContext.Provider>;
}

export function useAdminTime() {
  const context = useContext(AdminTimeContext);
  if (!context) {
    throw new Error('useAdminTime must be used within an AdminTimeProvider');
  }
  return context;
}
